import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Target, Sparkles, Loader2, TrendingUp, Lock, Crown, Save, ChevronDown, ChevronUp,
} from "lucide-react";
import { Link } from "react-router-dom";

/**
 * DiagnosticScores — Pro-only Dashboard widget.
 *
 * • Form: 4 MCAT section inputs (118-132)
 * • Chart: color-coded bar per section (red <124, yellow 124-127, green 128+)
 * • Generate My Plan: runs Gemini, saves an on-user snapshot, renders inline
 */
const SECTION_ORDER = ["chem_phys", "cars", "bio_biochem", "psych_soc"];
const SHORT_LABEL = {
  chem_phys: "C/P",
  cars: "CARS",
  bio_biochem: "B/B",
  psych_soc: "P/S",
};
const COLOR_HEX = {
  red: "#c94a3b",
  yellow: "#c98a3b",
  green: "#4f7942",
  grey: "#9ca3af",
};

export default function DiagnosticScores() {
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [scores, setScores] = useState(null); // shaped payload from API
  const [form, setForm] = useState({ chem_phys: "", cars: "", bio_biochem: "", psych_soc: "" });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planOpen, setPlanOpen] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [scoresRes, planRes] = await Promise.all([
          api.get("/diagnostic-scores"),
          api.get("/diagnostic-plan/current").catch(() => ({ data: null })),
        ]);
        setScores(scoresRes.data);
        const filled = {};
        (scoresRes.data.sections || []).forEach((s) => {
          filled[s.id] = s.score ?? "";
        });
        setForm(filled);
        if (planRes.data && planRes.data.plan) setPlan(planRes.data);
      } catch (e) {
        if (e.response?.status === 402) setLocked(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of SECTION_ORDER) {
        const v = form[k];
        if (v === "" || v === null || v === undefined) {
          payload[k] = null;
        } else {
          const n = Number(v);
          if (Number.isNaN(n) || n < 118 || n > 132) {
            toast.error(`${SHORT_LABEL[k]} must be between 118 and 132.`);
            setSaving(false);
            return;
          }
          payload[k] = n;
        }
      }
      const r = await api.put("/diagnostic-scores", payload);
      setScores(r.data);
      toast.success("Scores saved.");
    } catch (e) {
      toast.error("Couldn't save scores — try again?");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/diagnostic-plan/generate");
      setPlan(r.data);
      setPlanOpen(true);
      toast.success("Your personalized plan is ready!");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "no_scores") {
        toast.error(d.message);
      } else {
        toast.error("Couldn't generate the plan — try again in a moment.");
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="pm-card p-6 flex items-center gap-3">
        <Loader2 size={16} className="animate-spin" style={{ color: "var(--text-muted)" }} />
        <span className="text-sm" style={{ color: "var(--text-muted)" }}>Loading your diagnostic scores…</span>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="pm-card p-6" data-testid="diagnostic-locked">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "#c98a3b22" }}
          >
            <Lock size={20} style={{ color: "#c98a3b" }} />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg mb-1">My Diagnostic Scores — Pro</div>
            <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
              Track your 4 MCAT section scores, get color-coded strengths + weaknesses,
              and let AI build a 14-day study plan focused on your lowest-scoring section.
            </p>
            <Link
              to="/app/plans"
              className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
              data-testid="diagnostic-upgrade-btn"
            >
              <Crown size={14} /> Upgrade to Pro
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const sections = scores?.sections || [];
  const anyEntered = sections.some((s) => typeof s.score === "number");

  return (
    <div className="pm-card p-6" data-testid="diagnostic-scores">
      <div className="flex items-center gap-2 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "var(--hl-blue)" }}
        >
          <Target size={18} style={{ color: "var(--primary)" }} />
        </div>
        <div>
          <div className="font-display font-bold text-lg">My Diagnostic Scores</div>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            Enter your 4 MCAT section scores (118–132). Red &lt;124 · Yellow 124–127 · Green 128+
          </p>
        </div>
      </div>

      {/* Score inputs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {SECTION_ORDER.map((sid) => {
          const s = sections.find((x) => x.id === sid) || {};
          const color = COLOR_HEX[s.color || "grey"];
          return (
            <div key={sid} className="space-y-1">
              <label className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>
                {SHORT_LABEL[sid]}
              </label>
              <input
                type="number"
                min={118}
                max={132}
                value={form[sid]}
                onChange={(e) => setForm({ ...form, [sid]: e.target.value })}
                placeholder="—"
                className="w-full px-3 py-2 rounded-xl border text-center text-lg font-bold"
                style={{
                  borderColor: color,
                  borderWidth: form[sid] ? "2px" : "1px",
                  color: form[sid] ? color : "var(--text-muted)",
                  background: "var(--paper)",
                }}
                data-testid={`diagnostic-input-${sid}`}
              />
            </div>
          );
        })}
      </div>

      {/* Color-coded bar chart */}
      {anyEntered && (
        <div className="space-y-2 mb-4" data-testid="diagnostic-chart">
          {sections.map((s) => {
            if (typeof s.score !== "number") return null;
            const pct = ((s.score - 118) / (132 - 118)) * 100;
            const color = COLOR_HEX[s.color];
            return (
              <div key={s.id} className="flex items-center gap-3">
                <div className="w-14 text-xs font-semibold text-right" style={{ color: "var(--text-secondary)" }}>
                  {SHORT_LABEL[s.id]}
                </div>
                <div className="flex-1 h-6 rounded-full overflow-hidden relative" style={{ background: "var(--muted)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: color }}
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-bold"
                    style={{ color: "var(--text)" }}
                  >
                    {s.score}
                  </div>
                </div>
              </div>
            );
          })}
          {scores.total && (
            <div className="text-xs text-right pt-1" style={{ color: "var(--text-muted)" }}>
              Total: <b style={{ color: "var(--text)" }}>{scores.total}</b> / 528
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="pm-btn-secondary inline-flex items-center gap-1.5 text-sm"
          data-testid="diagnostic-save-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Scores
        </button>
        <button
          onClick={generate}
          disabled={generating || !anyEntered}
          className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
          data-testid="diagnostic-generate-btn"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate My Plan
        </button>
      </div>

      {/* Plan */}
      {plan && plan.plan && (
        <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--border)" }} data-testid="diagnostic-plan">
          <button
            onClick={() => setPlanOpen((v) => !v)}
            className="w-full flex items-center justify-between mb-3"
          >
            <div className="flex items-center gap-2">
              <TrendingUp size={16} style={{ color: "var(--primary)" }} />
              <div className="font-display font-bold">
                14-Day Plan — Focus: {plan.plan.focus_section}
              </div>
            </div>
            {planOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {planOpen && (
            <>
              <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
                {plan.plan.summary}
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {(plan.plan.days || []).map((day) => (
                  <div
                    key={day.day}
                    className="p-3 rounded-xl"
                    style={{ background: "var(--muted)" }}
                    data-testid={`diagnostic-plan-day-${day.day}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: "var(--primary)", color: "white" }}
                      >
                        Day {day.day}
                      </span>
                      <span className="text-sm font-semibold">{day.theme}</span>
                    </div>
                    <ul className="text-sm ml-1 mb-1 space-y-0.5" style={{ color: "var(--text-secondary)" }}>
                      {(day.topics || []).map((t, i) => (
                        <li key={i}>• {t}</li>
                      ))}
                    </ul>
                    {day.note && (
                      <p className="text-xs italic" style={{ color: "var(--text-muted)" }}>
                        {day.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
